import { types, flow } from 'mobx-state-tree';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import { areIntervalsOverlapping } from 'date-fns';

import { getRootStore } from 'models/root';
import api from 'services/API';
import { stringUtilities, dateUtilities } from 'utils';
import { sortByAgeOptions } from 'config/skuFilters';
import { POSSKU } from 'models/types/pos-sku';

export const IGNORED_PAGE_SIZE = 20;

let ignoredRequestCounter = 0;
let ignoredPendingOffset = 0;

const getCategories = (array, field = 'category') =>
  uniqBy(array, field)
    .map(sku => sku[field])
    .filter(Boolean)
    .slice();

export const skuMappingStoreInitialState = {
  all: [],
  isLoaded: false,
  filters: {
    categories: {
      matched: [],
      unmatched: [],
      ignored: [],
    },
    subCategories: {
      matched: [],
      unmatched: [],
      ignored: [],
    },
    beverageIds: [],
    producerIds: [],
    containerIds: [],
    dateRange: {
      period: 'all',
      from: null,
      to: null,
    },
    ignoredBy: [],
    ignoredDatePeriod: '',
    sortedBy: 'age',
    orderBy: 'desc',
  },
  searchString: '',
  ignoredSearch: '',
  ignoredTotal: 0,
  isIgnoredLoading: false,
  isIgnoredLoaded: false,
  ignoredRequestId: 0,
};

export const skuMappingStore = types
  .model({
    allActive: types.array(POSSKU),
    allIgnored: types.array(POSSKU),
    isLoaded: types.boolean,
    filters: types.model({
      categories: types.model({
        matched: types.array(types.string),
        unmatched: types.array(types.string),
        ignored: types.array(types.string),
      }),
      subCategories: types.model({
        matched: types.array(types.string),
        unmatched: types.array(types.string),
        ignored: types.array(types.string),
      }),
      beverageIds: types.array(types.number),
      producerIds: types.array(types.number),
      containerIds: types.array(types.number),
      dateRange: types.model({
        period: types.string,
        from: types.maybeNull(types.string),
        to: types.maybeNull(types.string),
      }),
      ignoredBy: types.array(types.integer),
      ignoredDatePeriod: types.string,
      sortedBy: types.enumeration('sortedBy', [
        'age',
        'item',
        'category',
        'latest_price',
        'beverage',
        'serving_size',
      ]),
      orderBy: types.enumeration('orderBy', ['asc', 'desc']),
    }),
    searchString: types.string,
    ignoredSearch: types.string,
    ignoredTotal: types.number,
    isIgnoredLoading: types.boolean,
    isIgnoredLoaded: types.boolean,
    ignoredRequestId: types.number,
  })
  .views(self => ({
    get allUnmatchedPOS() {
      let filtered = self.allActive;
      if (self.searchString) {
        const searchArray = self.searchString.toLowerCase().split(' ');
        filtered = self.allActive.filter(pos => {
          return searchArray.every(
            element =>
              pos?.item?.toLowerCase().includes(element) ||
              pos?.category?.toLowerCase().includes(element) ||
              pos?.subcategory?.toLowerCase().includes(element),
          );
        });
      }

      return orderBy(
        filtered.filter(sku => sku.sku_id === null),
        ['earliest_sale_at', 'category', 'subcategory'],
        ['asc', 'asc', 'asc'],
      );
    },

    get unmatchedPOS() {
      return self.filteredItems(self.allUnmatchedPOS, 'unmatched');
    },

    get allUnmatchedBeverages() {
      const root = getRootStore();

      const beverages = self.showHiddenBeverages
        ? root.beveragesStore.allExcludingPrototypes
        : root.beveragesStore.sortedBeverages;

      return orderBy(beverages, ['_producers_name', '_name'], ['asc', 'asc']);
    },

    get unmatchedBeverages() {
      return self.allUnmatchedBeverages;
    },

    get allMatched() {
      const { skuStore } = getRootStore();
      const skuMap = new Map(skuStore.all.map(sku => [sku.id, sku]));

      return self.allActive
        .filter(pos => pos.sku_id)
        .map(pos => ({
          ...pos,
          sku: skuMap.get(Number(pos.sku_id)),
        }));
    },

    get matchedBeverages() {
      const result = [];
      const seen = new Set();
      for (const pos of self.allMatched) {
        const beverage = pos.sku?.beverage;
        if (beverage && !seen.has(beverage.id)) {
          seen.add(beverage.id);
          result.push(beverage);
        }
      }
      return result;
    },

    get matchedProducers() {
      const result = [];
      const seen = new Set();
      for (const pos of self.allMatched) {
        const producer = pos.sku?.beverage;
        const producerId = producer?.producer_id;
        if (producer && producerId && !seen.has(producerId)) {
          seen.add(producerId);
          result.push(producer);
        }
      }
      return result;
    },

    get matchedContainers() {
      const result = [];
      const seen = new Set();
      for (const pos of self.allMatched) {
        const container = pos.sku?.container;
        if (container && !seen.has(container.id)) {
          seen.add(container.id);
          result.push(container);
        }
      }
      return result;
    },

    get groupedAllMatched() {
      const matched = [];

      let filtered = self.filteredItems(self.allMatched, 'matched');

      const groupedBySKU = groupBy(filtered, 'sku_id');

      const sortedByList =
        self.filters.sortedBy === 'category'
          ? ['category', 'subcategory']
          : [self.filters.sortedBy];
      const orderByList = Array(sortedByList.length).fill(self.filters.orderBy);

      Object.keys(groupedBySKU).forEach(sku_id => {
        const sku = groupedBySKU[sku_id][0].sku;
        if (sku && !self.showHiddenBeverages) {
          matched.push({
            id: new Date().getTime(),
            pos: orderBy(groupedBySKU[sku_id], sortedByList, orderByList),
            beverage: {
              ...sku.beverage,
              _containers_name: sku._containers_name,
              _container_id: sku.container_id,
            },
          });
        }
      });

      return matched;
    },

    get matched() {
      return self.filterGroupedMatched(self.groupedAllMatched).filter(sku => {
        const isMatchingBartrackItems = stringUtilities.matchSome(self.searchString, [
          sku.beverage._producers_name,
          sku.beverage._name,
        ]);

        const isMatchingPOSItems = sku.pos.some(posItem =>
          stringUtilities.matchSome(self.searchString, [
            posItem.item,
            posItem.category,
            posItem.subcategory,
          ]),
        );

        return isMatchingBartrackItems || isMatchingPOSItems;
      });
    },

    get matchedPOS() {
      return self.sortNestedPOS(self.matched);
    },

    get allIgnoredPOS() {
      return self.allIgnored;
    },

    get ignoredPOS() {
      return self.filteredItems(self.allIgnoredPOS, 'ignored');
    },

    getItemsByType(type) {
      switch (type) {
        case 'matched':
          return self.matchedPOS;
        case 'unmatched':
          return self.unmatchedPOS;
        case 'ignored':
          return self.ignoredPOS;
        default:
          return [];
      }
    },

    get isFiltersChanged() {
      const { dateRange, sortedBy, orderBy } = skuMappingStoreInitialState.filters;

      const commonConditions = Boolean(
        dateRange.period !== self.filters.dateRange.period ||
          sortedBy !== self.filters.sortedBy ||
          orderBy !== self.filters.orderBy,
      );

      const typeConditions = type =>
        Boolean(
          self.allCategories[type].length !== self.filters.categories[type].length ||
            self.allSubCategories[type].length !== self.filters.subCategories[type].length,
        );

      return {
        matched:
          typeConditions('matched') ||
          commonConditions ||
          self.filters.beverageIds.length !== self.matchedBeverages.length ||
          self.filters.producerIds.length !== self.matchedProducers.length ||
          self.filters.containerIds.length !== self.matchedContainers.length,
        unmatched: typeConditions('unmatched') || commonConditions,
        ignored:
          typeConditions('ignored') ||
          commonConditions ||
          self.filters.ignoredBy.length > 0 ||
          Boolean(self.filters.ignoredDatePeriod),
      };
    },

    get allCategories() {
      return {
        matched: getCategories(self.items.matched),
        unmatched: getCategories(self.items.unmatched),
        ignored: getCategories(self.items.ignored),
      };
    },

    get categories() {
      return {
        matched: getCategories(self.filteredWithoutCategory(self.items.matched, 'matched')),
        unmatched: getCategories(self.filteredWithoutCategory(self.items.unmatched, 'unmatched')),
        ignored: getCategories(self.filteredWithoutCategory(self.items.ignored, 'ignored')),
      };
    },

    get allSubCategories() {
      return {
        matched: getCategories(self.items.matched, 'subcategory'),
        unmatched: getCategories(self.items.unmatched, 'subcategory'),
        ignored: getCategories(self.items.ignored, 'subcategory'),
      };
    },

    get subCategories() {
      return {
        matched: getCategories(
          self.filteredWithoutCategory(self.items.matched, 'matched'),
          'subcategory',
        ),
        unmatched: getCategories(
          self.filteredWithoutCategory(self.items.unmatched, 'unmatched'),
          'subcategory',
        ),
        ignored: getCategories(
          self.filteredWithoutCategory(self.items.ignored, 'ignored'),
          'subcategory',
        ),
      };
    },

    // For each of categories / subCategories: true when every value present in the loaded rows is
    // selected, i.e. the user hasn't narrowed it. A selection that tracks "all" has to be extended
    // when a new page brings new values; a narrowed one must be left alone.
    get ignoredSelectionCoversLoaded() {
      const coversLoaded = (loaded, selected) => loaded.every(value => selected.includes(value));
      // Sub-categories under unselected categories are hidden by the category filter, not narrowed
      // by the user, so they must not count as "not selected".
      const selectedCategories = self.filters.categories.ignored;
      const subCategoriesInSelectedCategories = getCategories(
        self
          .filteredWithoutCategory(self.items.ignored, 'ignored')
          .filter(item => selectedCategories.includes(item.category)),
        'subcategory',
      );

      return {
        categories: coversLoaded(self.categories.ignored, selectedCategories),
        subCategories: coversLoaded(
          subCategoriesInSelectedCategories,
          self.filters.subCategories.ignored,
        ),
      };
    },

    get items() {
      return {
        matched: self.allActive.filter(sku => sku.sku_id),
        unmatched: self.allActive.filter(sku => sku.sku_id === null),
        ignored: self.allIgnored,
      };
    },

    get ignoredBy() {
      return [...new Set(self.allIgnored.map(sku => sku.ignored_by).filter(Boolean))];
    },

    filteredWithoutCategory(items, type) {
      let filtered = items;

      // The date range control isn't rendered on the ignored tab, so it must not filter there
      if (type !== 'ignored' && self.filters.dateRange.period !== 'all') {
        const { from, to } = self.filters.dateRange;

        const { to: toYesterDay } = dateUtilities.getDatesByPeriod('yesterday');
        const fromParsed = Date.parse(from);
        const toParsed = to ? Date.parse(to) : Date.parse(toYesterDay);

        filtered = filtered.filter(({ earliest_sale_at, latest_sale_at }) => {
          if (earliest_sale_at && latest_sale_at) {
            return areIntervalsOverlapping(
              { start: Date.parse(earliest_sale_at), end: Date.parse(latest_sale_at) },
              { start: fromParsed, end: toParsed },
              { inclusive: true },
            );
          }

          return (
            Date.parse(earliest_sale_at) >= fromParsed && Date.parse(earliest_sale_at) <= toParsed
          );
        });
      }

      if (self.filters.ignoredBy.length > 0) {
        filtered = filtered.filter(item => self.filters.ignoredBy.includes(item.ignored_by));
      }

      if (self.filters.ignoredDatePeriod) {
        const { from, to } = dateUtilities.getDatesByPeriod(self.filters.ignoredDatePeriod);

        // `to` is null for periods that run up to now (this month, this week, ytd...)
        filtered = filtered.filter(
          item =>
            item.ignored_at &&
            Date.parse(item.ignored_at) >= Date.parse(from) &&
            (!to || Date.parse(item.ignored_at) <= Date.parse(to)),
        );
      }

      return filtered;
    },
    filterGroupedMatched(items) {
      let filtered = items;

      if (self.filters.beverageIds.length > 0) {
        filtered = filtered.filter(({ beverage }) =>
          self.filters.beverageIds.includes(beverage.id),
        );
      }
      if (self.filters.producerIds.length > 0) {
        filtered = filtered.filter(({ beverage }) =>
          self.filters.producerIds.includes(beverage.producer_id),
        );
      }
      if (self.filters.containerIds.length > 0) {
        filtered = filtered.filter(({ beverage }) =>
          self.filters.containerIds.includes(beverage._container_id),
        );
      }

      return filtered;
    },
    filteredItems(items, type) {
      let filtered = items;

      if (!self.categories[type].every(e => self.filters.categories[type].includes(e))) {
        filtered = filtered.filter(item => self.filters.categories[type].includes(item.category));
      }

      if (!self.subCategories[type].every(e => self.filters.subCategories[type].includes(e))) {
        filtered = filtered.filter(item =>
          self.filters.subCategories[type].includes(item.subcategory),
        );
      }

      filtered = self.filteredWithoutCategory(filtered, type);

      const sortedBy =
        self.filters.sortedBy === 'age' ? sortByAgeOptions[type] : self.filters.sortedBy;
      const sortedByList = sortedBy === 'category' ? ['category', 'subcategory'] : [sortedBy];
      const orderByList = Array(sortedByList.length).fill(self.filters.orderBy);

      return orderBy(filtered, sortedByList, orderByList);
    },
    sortNestedPOS(items) {
      if (self.filters.sortedBy === 'beverage') {
        return orderBy(items, ['beverage._name', 'beverage._producers_name'], self.filters.orderBy);
      }

      if (self.filters.sortedBy === 'serving_size') {
        return orderBy(items, 'beverage._containers_name', self.filters.orderBy);
      }

      if (self.filters.sortedBy === 'category') {
        return orderBy(items, ['pos.0.category', 'pos.0.subcategory'], [self.filters.orderBy]);
      }

      return orderBy(items, item => item.pos[0][self.filters.sortedBy], [self.filters.orderBy]);
    },
  }))
  .actions(self => ({
    createMapping: flow(function* (mapping) {
      mapping.forEach(item => {
        if (!item.beverage_id) {
          delete item.beverage_id;
        }
        if (!item.container_id) {
          delete item.container_id;
        }
      });

      try {
        const response = yield api.matchSKU(mapping);
        response.data.result.forEach(item => self.updateActiveById(item));
      } catch (err) {
        return Promise.reject(err);
      }
    }),
    updateMapping: flow(function* (matchToUpdate, newItems) {
      const bartrackID = matchToUpdate.bartrack[0].id;

      const { pos } = newItems;
      if (Array.isArray(pos)) {
        const promises = pos.map(sku => {
          return self.connect(sku, { sku_id: bartrackID });
        });
        try {
          return yield Promise.all(promises);
        } catch (err) {
          return Promise.reject(err);
        }
      }
    }),
    fetchPOSSKUs: flow(function* fetchPOSSKUs() {
      try {
        const response = yield api.getPOSSKUs();
        const skus = response.data.result;
        self.isLoaded = true;

        const { false: allActive } = groupBy(skus, 'ignore');

        self.allActive.replace(allActive);
        self.setFilterCategories();

        self.isLoaded = true;
      } catch (err) {
        self.isLoaded = false;
        return Promise.reject(err);
      }
    }),
    patchPOSSKU: flow(function* patchPOSSKU(id, patch) {
      try {
        return yield api.patchPOSSKU(id, patch);
      } catch (err) {
        return Promise.reject(err);
      }
    }),
    patchPOSSKUs: flow(function* patchPOSSKU(body, params) {
      try {
        return yield api.patchPOSSKUs(body, params);
      } catch (err) {
        return Promise.reject(err);
      }
    }),
    connect: flow(function* connect(item, patch) {
      try {
        const response = yield self.patchPOSSKU(item.id, patch);
        const updatedItem = response.data.row;
        self.updateActiveById(updatedItem);
        return response;
      } catch (err) {
        return Promise.reject(err);
      }
    }),
    fetchIgnoredPOSSKUs: flow(function* fetchIgnoredPOSSKUs({ offset = 0 } = {}) {
      if (offset > 0 && self.isIgnoredLoading) return;

      const requestId = ++ignoredRequestCounter;
      ignoredPendingOffset = offset;
      self.ignoredRequestId = requestId;
      self.isIgnoredLoading = true;
      let needsReload = false;

      // The list is paginated, but only `item` (search) is sent to the server. Category, sub-category,
      // date range, sort and the hidden ignoredBy / ignoredDatePeriod filters are applied client-side
      // (filteredItems) to the loaded pages only.
      try {
        const response = yield api.getPosSKUsIgnored({
          item: self.ignoredSearch,
          limit: IGNORED_PAGE_SIZE,
          offset,
        });

        if (self.ignoredRequestId !== requestId) return;

        const { result, total } = response.data;

        const selectionCoveredLoaded = self.ignoredSelectionCoversLoaded;

        if (offset === 0) {
          self.allIgnored.replace(result);
          self.ignoredTotal = total;
        } else {
          const loadedIds = new Set(self.allIgnored.map(sku => sku.id));
          const newRows = result.filter(sku => !loadedIds.has(sku.id));

          self.allIgnored.push(...newRows);
          self.ignoredTotal = total;
          // Offsets shift when rows are (un)ignored elsewhere, so a page without new rows doesn't
          // prove the list is complete. Paginating again from the start avoids re-requesting
          // the same offset forever.
          needsReload = !newRows.length && total > self.allIgnored.length;
        }
        self.syncIgnoredFilters(selectionCoveredLoaded);

        self.isIgnoredLoaded = true;
        self.isIgnoredLoading = false;
      } catch (err) {
        // A superseded request must not surface its failure or clear the newer request's state.
        if (self.ignoredRequestId === requestId) {
          self.isIgnoredLoading = false;
          return Promise.reject(err);
        }
      }

      if (needsReload) {
        yield self.fetchIgnoredPOSSKUs({ offset: 0 });
      }
    }),
    // Responses to requests sent before a mutation can restore removed rows or skip shifted ones.
    // Returns true when the dropped request was the first page, which then has to be re-sent.
    invalidateIgnoredFetch() {
      if (!self.isIgnoredLoading) return false;

      self.ignoredRequestId = ++ignoredRequestCounter;
      self.isIgnoredLoading = false;
      return ignoredPendingOffset === 0;
    },
    reloadIgnoredAfterMutation: flow(function* reloadIgnoredAfterMutation() {
      try {
        yield self.fetchIgnoredPOSSKUs({ offset: 0 });
      } catch (err) {
        console.error(err);
      }
    }),
    ignore: flow(function* ignore(items) {
      try {
        const response = yield api.ignoreSKU(items.map(item => item.id));
        const updatedItems = response.data.result;
        items.forEach(item => self.allActive.remove(item));
        if (self.isIgnoredLoaded) {
          updatedItems.forEach(item => self.allIgnored.push(item));
          self.ignoredTotal += updatedItems.length;
        }
        if (self.invalidateIgnoredFetch()) {
          yield self.reloadIgnoredAfterMutation();
        }
        return response;
      } catch (err) {
        console.error(err);
        return Promise.reject(err);
      }
    }),
    unignore: flow(function* unignore(ids) {
      try {
        const response = yield api.unignoreSKU(ids);
        const updatedItems = response.data.result;
        const updatedItemsIds = updatedItems.map(item => item.id);
        const updatedNodes = self.allIgnored.filter(item => updatedItemsIds.includes(item.id));
        updatedNodes.forEach(node => {
          self.allIgnored.remove(node);
        });
        self.ignoredTotal = Math.max(0, self.ignoredTotal - updatedNodes.length);
        self.allActive.push(...updatedItems);
        if (self.invalidateIgnoredFetch()) {
          yield self.reloadIgnoredAfterMutation();
        }
        return response;
      } catch (err) {
        console.error(err);
        return Promise.reject(err);
      }
    }),
    unlink: flow(function* (ids) {
      try {
        const response = yield api.unmatchSKU([...ids]);

        response.data.result.forEach(posSKU => self.updateActiveById(posSKU));
        return response;
      } catch (err) {
        return Promise.reject(err);
      }
    }),
    updateActiveById(item) {
      const itemIndex = self.allActive.findIndex(sku => sku.id === item.id);
      if (itemIndex >= 0) {
        self.allActive[itemIndex] = item;
      }
    },
    setFilters(filters, type) {
      if (filters === null) {
        self.filters = {
          ...skuMappingStoreInitialState.filters,
          categories: self.allCategories,
          subCategories: self.allSubCategories,
          beverageIds: self.matchedBeverages.map(beverage => beverage.id),
          producerIds: self.matchedProducers.map(producer => producer.producer_id),
          containerIds: self.matchedContainers.map(container => container.id),
        };
      } else {
        if (type) {
          self.filters = {
            ...self.filters,
            categories: {
              ...self.filters.categories,
              [type]: filters.categories || self.filters.categories[type],
            },
            subCategories: {
              ...self.filters.subCategories,
              [type]: filters.subCategories || self.filters.subCategories[type],
            },
          };
        }

        self.filters = {
          ...self.filters,
          beverageIds: filters.beverageIds || self.filters.beverageIds,
          producerIds: filters.producerIds || self.filters.producerIds,
          containerIds: filters.containerIds || self.filters.containerIds,
          dateRange: filters.dateRange || self.filters.dateRange,
          dateFrom: filters.dateFrom || self.filters.dateFrom,
          dateTo: filters.dateTo || self.filters.dateTo,
          sortedBy: filters.sortedBy || self.filters.sortedBy,
          orderBy: filters.orderBy || self.filters.orderBy,
          ignoredBy: filters.ignoredBy || self.filters.ignoredBy,
          ignoredDatePeriod: filters.ignoredDatePeriod ?? self.filters.ignoredDatePeriod,
        };
      }
    },

    resetFilters() {
      self.setFilters(null);
    },
    setSearch(value) {
      self.searchString = value;
    },
    setIgnoredSearch(value) {
      self.ignoredSearch = value;
    },
    syncIgnoredFilters({ categories, subCategories }) {
      if (categories) {
        self.filters.categories.ignored = self.allCategories.ignored;
      }
      if (subCategories) {
        self.filters.subCategories.ignored = self.allSubCategories.ignored;
      }
    },
    setFilterCategories() {
      self.filters.categories = self.categories;
      self.filters.subCategories = self.subCategories;
      self.filters.beverageIds = self.matchedBeverages.map(beverage => beverage.id);
      self.filters.producerIds = self.matchedProducers.map(producer => producer.producer_id);
      self.filters.containerIds = self.matchedContainers.map(container => container.id);
    },
    setActivePOSSKUs(skus) {
      self.allActive.replace(skus);
      self.setFilterCategories();

      self.isLoaded = true;
    },
  }));
